import { Col, FormInput, Row, FormGroup } from '@snsw/react-component-library';
import { useOptionallyControlledValue } from 'components/Utils/useOptionallyControlledValue';
import React, { useMemo } from 'react';
import Select from 'react-select';
import styled, { createGlobalStyle } from 'styled-components';

export interface MonthYearDateProps {
  id?: string;
  name: string;
  defaultValue?: MonthYearValue;
  value?: MonthYearValue;
  onChange?: (next: MonthYearValue) => void;
}

export interface MonthYearValue {
  month: string;
  year: string;
}

interface SelectOption {
  label: string | null;
  value: string | null;
}

const Label = styled.label`
  font-size: 0.8rem;
`;

const DropDownGlobalStyles = createGlobalStyle`
  .drop__control, drop__control--is-focused {
    &&&,
    &:hover {
      border: none;
      outline: none;
      box-shadow: none;
    }
  }
`;

const StyledRow = styled(Row)`
  .dropdown {
    width: 100%;
    max-width: 100%;
    font-size: 1rem;
    background-color: rgb(255, 255, 255);
    border: 2px solid rgb(100, 105, 116);
    border-radius: 6px;
    padding-top: 3px;
    margin-top: 0.25rem;
    transition: border-color 0.25s ease 0s, background-color 0.25s ease 0s;
    height: 3rem;
    appearance: none;
    color: rgb(36, 41, 52);
    background-repeat: no-repeat;
    background-position: right 1.125rem center;
    cursor: pointer;
  }
`;

const useMonthOptions = (): SelectOption[] => {
  return useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en', { month: 'long' }); // sets words language (optional if current locale is to be used)
    const getMonthName = (monthIndex: number) => formatter.format(new Date(new Date().getFullYear(), monthIndex));
    const monthNumbers: number[] = [];
    for (let i = 0; i < 12; i++) {
      monthNumbers.push(i);
    }
    const months = monthNumbers.map<string>(getMonthName);

    return months.map((m) => ({
      value: m,
      label: m,
    }));
  }, []);
};

const getInitialValue = (...initialValues: MonthYearValue[]) =>
  initialValues.find((v) => !!(v?.month || v?.year)) || {
    month: '',
    year: '',
  };

export const MonthYearDate = ({ id, name, defaultValue, value: controlledValue, onChange }: MonthYearDateProps) => {
  const [value, handleValueChange] = useOptionallyControlledValue<MonthYearValue | undefined>(
    () => getInitialValue(defaultValue, controlledValue),
    controlledValue,
    onChange
  );

  const monthOptions = useMonthOptions();

  const selectedMonthOption = monthOptions.find((opt) => opt.value === value?.month);

  const handleMonthChange = (next: SelectOption) => handleValueChange({ ...value, month: next.value });

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    handleValueChange({ ...value, year: event.target.value });

  const baseId = id ?? name;
  const getFieldId = (field: string) => `${baseId}-${field}`;
  const getFieldName = (field: string) => `${name}-${field}`;

  return (
    <StyledRow>
      <Col span={9}>
        <FormGroup label="Month">
          <Select
            aria-label="Month"
            id={getFieldId('month')}
            name={getFieldName('month')}
            classNamePrefix={'drop'}
            className="dropdown"
            options={monthOptions}
            value={selectedMonthOption}
            onChange={handleMonthChange}
          />
        </FormGroup>
      </Col>

      <Col span={3}>
        <FormInput
          id={getFieldId('year')}
          name={getFieldName('year')}
          label="Year"
          value={value.year}
          onChange={handleYearChange}
        />
      </Col>

      <DropDownGlobalStyles />
    </StyledRow>
  );
};
